import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../../config/config';
import IndividualCard from '../../components/IndividualCard/IndividualCard';
import ContactUs from '../../components/ContactUs/ContactUs';
import styles from './SalvationPage.module.css';

interface SalvationVerse {
  reference: string;
  verses: any; // Can be string or portable text
  explanation: string;
  detailedTeaching: {
    title: string;
    date: string;
    content: any; // Can be string or portable text
    type: 'salvation';
    references: string[];
  };
}

const SalvationPage: React.FC = () => {
  const [salvationVerses, setSalvationVerses] = useState<SalvationVerse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to extract text from the first block of verses portable text
  const getVerseSummary = (portableTextBlocks: any): string => {
    if (typeof portableTextBlocks === 'string') {
      return portableTextBlocks;
    }
    
    if (!portableTextBlocks || !Array.isArray(portableTextBlocks) || portableTextBlocks.length === 0) {
      return 'No verse text available.';
    }
    
    const firstBlock = portableTextBlocks[0];
    if (firstBlock && firstBlock.children && Array.isArray(firstBlock.children) && firstBlock.children.length > 0) {
      return firstBlock.children
        .filter((child: any) => child.text)
        .map((child: any) => child.text)
        .join(' ');
    }
    
    return 'Verse text not in expected format.';
  };

  useEffect(() => {
    setLoading(true);
    
    // Fetch salvation posts from Sanity.io
    const fetchSalvationPosts = async () => {
      try {
        const response = await axios.get(`${config.API_BASE_URL}/salvation-posts`);
        if (response.data && response.data.length > 0) {
          // Sort fetched posts by date
          const sortedPosts = response.data.sort((a: any, b: any) => 
            new Date(b.date).getTime() - new Date(a.date).getTime()
          );
          
          // Transform the posts to match the SalvationVerse interface
          const formattedVerses: SalvationVerse[] = sortedPosts.map((post: any) => ({
            reference: post.reference || post.title,
            verses: post.verses || '',
            explanation: post.content && Array.isArray(post.content) && post.content[0]?.children ? 
              post.content[0].children.map((child: any) => child.text).join(' ') : 
              'No explanation available',
            detailedTeaching: {
              title: post.title,
              date: post.date,
              content: post.content,
              type: 'salvation',
              references: post.references || []
            }
          }));
          
          setSalvationVerses(formattedVerses);
          setError(null);
        } else {
          setError('No salvation posts found.');
          setSalvationVerses([]);
        }
      } catch (error) {
        console.error('Error fetching salvation posts:', error);
        setError('Failed to fetch salvation posts. Please try again later.');
        setSalvationVerses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSalvationPosts();
  }, []);

  if (loading) return <div className={styles.loading}>Loading salvation verses...</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (salvationVerses.length === 0) return <div className={styles.empty}>No salvation verses found.</div>;

  return (
    <div className={styles['salvation-page']}>
      <section className={styles['salvation-intro']}>
        <h1>Salvation Through Christ</h1>
        <p className={styles['salvation-description']}>
          Salvation is the free gift of God to all who believe in Jesus Christ. On this page, we explore
          what the Bible teaches about salvation, how to receive it, and what it means for our lives.
        </p>
      </section>

      <section className={styles['salvation-verses']}>
        <h2>Key Bible Verses About Salvation</h2>
        <p className={styles['click-instruction']}>Click on any verse to view its detailed teaching</p>
        <div className={styles['verses-container']}>
          {salvationVerses.map((verse, index) => (
            <Link 
              to={`/salvation/${encodeURIComponent(verse.reference)}`}
              key={index}
              className={styles['verse-link']}
            >
              <div className={`${styles['verse-card']} ${styles['clickable']}`}>
                <IndividualCard
                  reference={verse.reference}
                  verses={getVerseSummary(verse.verses)}
                  date={verse.detailedTeaching.date}
                />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <ContactUs />
    </div>
  );
};

export default SalvationPage;
